import React from 'react';
import { InlineContentEditor } from '@/components/admin/InlineContentEditor';

const defaultRulesContent = `<div class="space-y-6">
  <h1 class="text-3xl font-bold text-foreground mb-4">Minor Baseball Talks – Community Rules & Posting Guidelines</h1>
  
  <p class="text-muted-foreground">Welcome to Minor Baseball Talks – a forum created to foster respectful, insightful, and engaging conversations about youth baseball. These rules outline expectations for all members to ensure a positive and productive environment for players, parents, coaches, and fans.</p>

  <div class="mt-8">
    <h2 class="text-2xl font-semibold text-foreground mb-3">⚾ General Community Guidelines</h2>
    <p class="text-muted-foreground">By using this forum, you agree to follow these rules, as well as our <a href="/terms" class="text-primary hover:underline">Terms of Use</a> and <a href="/privacy" class="text-primary hover:underline">Privacy Policy</a>. These rules apply to all members and guests, and exist to promote respectful dialogue and protect the community.</p>
  </div>

  <div class="mt-8">
    <h2 class="text-2xl font-semibold text-foreground mb-3">📌 Posting Guidelines</h2>
    <p class="text-muted-foreground mb-4">Before posting, please keep the following in mind:</p>

    <div class="space-y-6">
      <div>
        <h3 class="text-xl font-semibold text-foreground mb-2">Use Constructive Language</h3>
        <p class="text-muted-foreground mb-2">Share your opinions using examples and insights. Focus on decisions and situations, not personal attacks.</p>
        <p class="font-semibold text-foreground mb-1">Examples:</p>
        <ul class="list-none space-y-2 ml-4">
          <li class="text-muted-foreground">❌ "The coach is an idiot for leaving the pitcher in."</li>
          <li class="text-muted-foreground">✅ "The pitcher had thrown 90+ pitches and was showing signs of fatigue. Leaving him in for another inning was a questionable decision given the circumstances."</li>
          <li class="text-muted-foreground mt-2">❌ "That umpire had no clue what he was doing."</li>
          <li class="text-muted-foreground">✅ "There were several close calls at the plate that could have gone either way. The strike zone seemed inconsistent in the late innings."</li>
        </ul>
      </div>

      <div>
        <h3 class="text-xl font-semibold text-foreground mb-2">Avoid Personal Attacks</h3>
        <p class="text-muted-foreground">Criticism of decisions, strategies, and game situations is welcome – personal attacks on individuals, parents, players, coaches, or businesses are not. Keep discussions respectful and on-topic.</p>
      </div>

      <div>
        <h3 class="text-xl font-semibold text-foreground mb-2">No Repetitive Posts</h3>
        <p class="text-muted-foreground">Avoid posting the same opinion multiple times or repeating content already shared without adding new insights or value to the conversation.</p>
      </div>

      <div>
        <h3 class="text-xl font-semibold text-foreground mb-2">Don't Quote Problematic Posts</h3>
        <p class="text-muted-foreground">If you quote a post that violates the rules and it gets removed, your quote may also be removed. Stick to your own words and perspectives.</p>
      </div>

      <div>
        <h3 class="text-xl font-semibold text-foreground mb-2">Do Not Single Out Children</h3>
        <p class="text-muted-foreground mb-2">Avoid criticizing specific youth players by name or number. Remember, these are kids learning and developing.</p>
        <p class="font-semibold text-foreground mb-1">Examples:</p>
        <ul class="list-none space-y-2 ml-4">
          <li class="text-muted-foreground">❌ "#7 in center field was terrible and cost us the game."</li>
          <li class="text-muted-foreground">✅ "The outfield defense struggled last night with several missed catches on routine fly balls."</li>
          <li class="text-muted-foreground mt-2">❌ "The shortstop can't field to save his life."</li>
          <li class="text-muted-foreground">✅ "The infield had trouble with ground balls in the early innings but improved as the game went on."</li>
        </ul>
      </div>

      <div>
        <h3 class="text-xl font-semibold text-foreground mb-2">Use Initials or General Descriptions When Possible</h3>
        <p class="text-muted-foreground mb-2">When referring to parents, coaches, or other adults, use initials or general terms unless the person is a public figure (e.g., MLB player, professional coach, or someone already in the public domain).</p>
        <p class="font-semibold text-foreground mb-1">Examples:</p>
        <ul class="list-none space-y-1 ml-4">
          <li class="text-muted-foreground">• Use "Coach M" instead of full names</li>
          <li class="text-muted-foreground">• Use "one of the parents" or "a few parents" instead of naming individuals</li>
          <li class="text-muted-foreground">• Public figures (professional players, coaches, scouts) can be named</li>
        </ul>
      </div>

      <div>
        <h3 class="text-xl font-semibold text-foreground mb-2">Stay On Topic</h3>
        <p class="text-muted-foreground">Keep discussions relevant to baseball. Off-topic posts may be moved or removed to maintain forum organization.</p>
      </div>

      <div>
        <h3 class="text-xl font-semibold text-foreground mb-2">No Commercial Solicitation</h3>
        <p class="text-muted-foreground">Do not use the forum to advertise businesses, services, or products without prior approval from administrators. Sharing relevant resources is fine, but blatant advertising is not permitted.</p>
      </div>
    </div>
  </div>

  <div class="mt-8">
    <h2 class="text-2xl font-semibold text-foreground mb-3">🔒 Moderation & Responsibility</h2>
    <p class="text-muted-foreground mb-2">Posts are reviewed for the greater good of the community, based on the rules above.</p>
    <ul class="list-disc space-y-1 ml-6 text-muted-foreground">
      <li><strong>You are responsible for everything you post</strong> – even as a guest user</li>
      <li>While guest posts may appear anonymous, your IP address and post data are logged for moderation and safety purposes</li>
      <li>Moderators may remove, edit, or restrict posts that violate these rules without prior notice</li>
      <li>Repeated violations may result in temporary or permanent bans</li>
      <li>All moderation decisions are final, though you may appeal if you believe an error was made</li>
    </ul>
  </div>

  <div class="mt-8">
    <h2 class="text-2xl font-semibold text-foreground mb-3">🚩 Reporting Posts</h2>
    <p class="text-muted-foreground mb-2">To report a post that violates these rules:</p>
    <ol class="list-decimal space-y-1 ml-6 text-muted-foreground mb-4">
      <li><strong>Click the flag icon</strong> below the post</li>
      <li>The post will be immediately hidden from public view pending moderator review</li>
      <li><strong>Provide a brief explanation</strong> of your concern when prompted</li>
      <li>Our moderation team will review within 24 hours</li>
    </ol>
    <p class="font-semibold text-foreground mb-1">Please note:</p>
    <ul class="list-disc space-y-1 ml-6 text-muted-foreground">
      <li>A flagged post is not automatically deleted – it is reviewed by moderators</li>
      <li>Disagreeing with someone's opinion does not constitute a rule violation</li>
      <li>Abuse of the reporting system may result in restrictions on your account</li>
      <li>All reports are logged and reviewed</li>
    </ul>
  </div>

  <div class="mt-8">
    <h2 class="text-2xl font-semibold text-foreground mb-3">⚖️ Appeals Process</h2>
    <p class="text-muted-foreground mb-2">If you believe your post was removed in error or you disagree with a moderation decision:</p>
    <ol class="list-decimal space-y-1 ml-6 text-muted-foreground">
      <li>Contact administrators through the "Contact Us" link</li>
      <li>Reference the specific post or decision in question</li>
      <li>Explain why you believe the decision should be reconsidered</li>
      <li>Appeals are typically reviewed within 48 hours</li>
    </ol>
  </div>

  <div class="mt-8">
    <h2 class="text-2xl font-semibold text-foreground mb-3">🎯 Community Standards</h2>
    <p class="text-muted-foreground mb-2">We strive to maintain a welcoming environment where:</p>
    <ul class="list-disc space-y-1 ml-6 text-muted-foreground">
      <li>Youth baseball discussions are constructive and educational</li>
      <li>Parents can share experiences and seek advice</li>
      <li>Coaches can exchange strategies and best practices</li>
      <li>Players can learn and engage with the baseball community</li>
      <li>Respectful debate and differing opinions are encouraged</li>
    </ul>
    <p class="text-muted-foreground mt-3"><strong>Remember:</strong> The goal is to create a positive space for everyone who loves youth baseball. When in doubt, ask yourself: "Would I say this to someone's face at the ballpark?"</p>
  </div>

  <div class="mt-8">
    <h2 class="text-2xl font-semibold text-foreground mb-3">📬 Contact Us</h2>
    <p class="text-muted-foreground">Have a question, concern, or feedback about the forum? Use the "Contact Us" link at the bottom of any page to reach out to the site administrators.</p>
  </div>

  <hr class="my-8 border-border" />

  <p class="text-center text-muted-foreground font-semibold">Thank you for being part of Minor Baseball Talks! Together we can build a supportive and engaging community for youth baseball families everywhere.</p>
</div>`;

const ForumRules = () => {
  return (
    <InlineContentEditor
      settingKey="forum_rules_content"
      title="Forum Rules"
      defaultContent={defaultRulesContent}
    />
  );
};

export default ForumRules;